import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronsUpDown, Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { itemOptions } from '../lib/contracts'
import type { CostTree } from '../lib/tree'

const LIMIT = 100

interface Props {
  tree: CostTree; excludeIds: string[]
  onlyIds?: string[]
  /** Offer groups as well as items (an RFQ's budget item or group). */
  withGroups?: boolean
  disabled?: boolean
  onPick: (nodeId: string) => void
  /** select: a compact field showing `label` (the mapped item), or a muted prompt without it. */
  variant?: 'add' | 'select'
  label?: string
}

/** "Přidat položku": a searchable list of the budget's items (groups too with `withGroups`), without those already on a line. */
const ItemPicker: React.FC<Props> = ({ tree, excludeIds, onlyIds, withGroups = false, disabled, onPick, label, variant = 'add' }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  // Computed only while open: the price editor renders one picker per row.
  const options = useMemo(() => (open ? itemOptions(tree, excludeIds, query, onlyIds, withGroups) : []), [open, tree, excludeIds, query, onlyIds, withGroups])
  // modal: the picker lives inside line dialogs, whose scroll lock would otherwise swallow the wheel over the portaled list.
  return (
    <Popover modal open={open} onOpenChange={(next) => { setOpen(next); if (!next) setQuery('') }}>
      <PopoverTrigger asChild>
        {variant === 'select' ? (
          <Button variant="outline" size="sm" className="h-8 w-full justify-between font-normal" disabled={disabled}>
            <span className={cn('truncate', !label && 'text-muted-foreground')}>{label ?? t('CostControl_item_picker_choose')}</span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        ) : (
          <Button variant="outline" size="sm" className="h-8" disabled={disabled}>
            <Plus className="h-4 w-4" />
            {label ?? t('CostControl_item_picker_add')}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[28rem] p-2">
        <div className="relative mb-2">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('CostControl_item_picker_search')} className="h-8 pl-8 text-sm" />
        </div>
        <ul className="max-h-72 overflow-y-auto">
          {options.slice(0, LIMIT).map((o) => (
            <li key={o.id}>
              <button
                type="button"
                className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
                onClick={() => { onPick(o.id); setOpen(false); setQuery('') }}
              >
                {o.path && <div className="truncate text-xs text-muted-foreground">{o.path}</div>}
                <div className="truncate">{o.code ? `${o.code} · ${o.name}` : o.name}</div>
              </button>
            </li>
          ))}
        </ul>
        {options.length === 0 && <p className="px-2 py-1.5 text-sm text-muted-foreground">{t('CostControl_item_picker_empty')}</p>}
        {options.length > LIMIT && <p className="px-2 pt-1 text-xs text-muted-foreground">{t('CostControl_item_picker_more', { count: LIMIT })}</p>}
      </PopoverContent>
    </Popover>
  )
}
export default ItemPicker
